import React from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Dimensions,
} from 'react-native';
import { Colors } from '../../../../constants/Colors';
import { ProductInCart } from '../../../../store/BagStore';

export type UnavailableReason = 
  | 'not_found' 
  | 'insufficient_stock' 
  | 'price_mismatch' 
  | 'catalog_not_found'
  | 'combo_error'
  | 'combo_processing_error'
  | 'combo_limit_exceeded';

export interface ComboLimitDetails {
  max_by_budget: number;
  max_by_stock: number;
  max_by_client: number;
  combos_ordered_this_month: number;
  limitante: string;
}

export interface UnavailableProduct {
  description?: string;
  sku: string;
  availableQty?: number;
  requestedQty?: number;
  reason: UnavailableReason;
  message: string;
  correctPrice?: number;
  sentPrice?: number;
  details?: ComboLimitDetails;
  product?: {
    SKU: string;
    DESCRICAO: string;
    COMPLEMENTO: string;
    PRECO: number;
    [key: string]: any;
  };
}

interface UnavailableModalProps {
  visible: boolean;
  unavailableProducts: UnavailableProduct[];
  cartProducts: ProductInCart[];
  onUpdateCart: () => void;
  onClearCart: () => void;
  onClose: () => void;
}

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const UnavailableModal: React.FC<UnavailableModalProps> = ({
  visible,
  unavailableProducts,
  cartProducts,
  onUpdateCart,
  onClearCart,
  onClose,
}) => {
  // Agrupa produtos por tipo de problema
  const groupedProducts = {
    priceIssues: unavailableProducts.filter(p => p.reason === 'price_mismatch'),
    stockIssues: unavailableProducts.filter(p => 
      p.reason === 'not_found' || p.reason === 'insufficient_stock'
    ),
    catalogIssues: unavailableProducts.filter(p => p.reason === 'catalog_not_found'),
    comboLimitIssues: unavailableProducts.filter(p => p.reason === 'combo_limit_exceeded'),
    comboErrors: unavailableProducts.filter(p => 
      p.reason === 'combo_error' || p.reason === 'combo_processing_error'
    ),
  };

  const hasPriceIssues = groupedProducts.priceIssues.length > 0;
  const hasStockIssues = groupedProducts.stockIssues.length > 0;
  const hasCatalogIssues = groupedProducts.catalogIssues.length > 0;
  const hasComboLimitIssues = groupedProducts.comboLimitIssues.length > 0;
  const hasComboErrors = groupedProducts.comboErrors.length > 0;

  // Verifica se há algo que pode ser atualizado
  const canUpdate = hasPriceIssues || 
    hasComboLimitIssues ||
    groupedProducts.stockIssues.some(p => p.reason === 'insufficient_stock' && (p.availableQty ?? 0) > 0);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  // Busca o nome do produto no carrinho pelo SKU
  const getProductName = (unavailableProduct: UnavailableProduct) => {
    const skuNormalized = unavailableProduct.sku.replace(/^0+/, '');
    
    const cartProduct = cartProducts.find(
      p => p.sku.replace(/^0+/, '') === skuNormalized
    );
    
    if (cartProduct) {
      const complemento = (cartProduct.complemento || '')
        .replace('- R$', '')
        .replace(/\\n/g, '')
        .trim();
      return `${cartProduct.descricao} ${complemento}`.trim();
    }
    
    if (unavailableProduct.product?.DESCRICAO) {
      const complemento = (unavailableProduct.product.COMPLEMENTO || '')
        .replace('- R$', '')
        .replace(/\\n/g, '')
        .trim();
      return `${unavailableProduct.product.DESCRICAO} ${complemento}`.trim();
    }
    
    return unavailableProduct.description || `SKU: ${unavailableProduct.sku}`;
  };

  // Traduz o limitante para português
  const getLimitanteText = (limitante: string) => {
    const translations: Record<string, string> = {
      'budget': 'orçamento da categoria',
      'stock': 'estoque dos componentes',
      'client_limit': 'limite mensal do cliente',
    };
    
    const parts = limitante.split(', ');
    return parts.map(p => translations[p] || p).join(', ');
  };

  const renderPriceIssueItem = (item: UnavailableProduct, index: number) => (
    <View key={`price-${item.sku}-${index}`} style={styles.itemCard}>
      <Text style={styles.itemName} numberOfLines={2}>
        {getProductName(item)}
      </Text>
      <View style={styles.priceCompare}>
        <View style={styles.priceRow}>
          <Text style={styles.priceLabel}>Preço no carrinho:</Text>
          <Text style={styles.oldPrice}>
            {formatCurrency(item.sentPrice ?? 0)}
          </Text>
        </View>
        <View style={styles.priceRow}>
          <Text style={styles.priceLabel}>Preço atualizado:</Text>
          <Text style={styles.newPrice}>
            {formatCurrency(item.correctPrice ?? 0)}
          </Text>
        </View>
      </View>
    </View>
  );

  const renderStockIssueItem = (item: UnavailableProduct, index: number) => (
    <View key={`stock-${item.sku}-${index}`} style={styles.itemCard}>
      <Text style={styles.itemName} numberOfLines={2}>
        {getProductName(item)}
      </Text>
      <Text style={styles.stockInfo}>
        {item.reason === 'not_found' 
          ? 'Produto sem estoque disponível'
          : item.availableQty === 0
            ? 'Estoque esgotado'
            : `Disponível: ${item.availableQty} unidade(s)`
        }
      </Text>
    </View>
  );

  const renderCatalogIssueItem = (item: UnavailableProduct, index: number) => (
    <View key={`catalog-${item.sku}-${index}`} style={styles.itemCard}>
      <Text style={styles.itemName} numberOfLines={2}>
        {getProductName(item)}
      </Text>
      <Text style={styles.catalogInfo}>
        Produto não disponível no catálogo
      </Text>
    </View>
  );

  const renderComboLimitItem = (item: UnavailableProduct, index: number) => (
    <View key={`combo-limit-${item.sku}-${index}`} style={styles.itemCard}>
      <Text style={styles.itemName} numberOfLines={2}>
        {getProductName(item)}
      </Text>
      <View style={styles.comboLimitInfo}>
        <View style={styles.quantityRow}>
          <Text style={styles.quantityLabel}>Solicitado:</Text>
          <Text style={styles.requestedQty}>{item.requestedQty ?? 0}</Text>
        </View>
        <View style={styles.quantityRow}>
          <Text style={styles.quantityLabel}>Disponível:</Text>
          <Text style={styles.availableQty}>{item.availableQty ?? 0}</Text>
        </View>
        {item.details?.limitante && (
          <Text style={styles.limitanteText}>
            Limitado por: {getLimitanteText(item.details.limitante)}
          </Text>
        )}
      </View>
    </View>
  );

  const renderComboErrorItem = (item: UnavailableProduct, index: number) => (
    <View key={`combo-error-${item.sku}-${index}`} style={styles.itemCard}>
      <Text style={styles.itemName} numberOfLines={2}>
        {getProductName(item)}
      </Text>
      <Text style={styles.comboErrorInfo}>
        Erro ao processar combo. Tente novamente.
      </Text>
    </View>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.iconContainer}>
              <Text style={styles.warningIcon}>⚠️</Text>
            </View>
            <Text style={styles.title}>Carrinho Desatualizado</Text>
            <Text style={styles.subtitle}>
              Alguns produtos precisam de atenção antes de continuar
            </Text>
          </View>

          {/* Content */}
          <ScrollView 
            style={styles.content}
            showsVerticalScrollIndicator={false}
          >
            {/* Seção de Limite de Combos */}
            {hasComboLimitIssues && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionIcon}>🎁</Text>
                  <Text style={styles.sectionTitle}>Limite de Combos</Text>
                </View>
                <Text style={styles.sectionDescription}>
                  A quantidade solicitada excede o limite disponível:
                </Text>
                {groupedProducts.comboLimitIssues.map(renderComboLimitItem)}
              </View>
            )}

            {/* Seção de Preços Divergentes */}
            {hasPriceIssues && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionIcon}>💰</Text>
                  <Text style={styles.sectionTitle}>Preços Atualizados</Text>
                </View>
                <Text style={styles.sectionDescription}>
                  Os preços dos produtos abaixo foram alterados:
                </Text>
                {groupedProducts.priceIssues.map(renderPriceIssueItem)}
              </View>
            )}

            {/* Seção de Problemas de Estoque */}
            {hasStockIssues && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionIcon}>📦</Text>
                  <Text style={styles.sectionTitle}>Estoque Indisponível</Text>
                </View>
                <Text style={styles.sectionDescription}>
                  Os produtos abaixo têm problemas de estoque:
                </Text>
                {groupedProducts.stockIssues.map(renderStockIssueItem)}
              </View>
            )}

            {/* Seção de Produtos Fora do Catálogo */}
            {hasCatalogIssues && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionIcon}>🚫</Text>
                  <Text style={styles.sectionTitle}>Fora do Catálogo</Text>
                </View>
                <Text style={styles.sectionDescription}>
                  Estes produtos não estão mais disponíveis:
                </Text>
                {groupedProducts.catalogIssues.map(renderCatalogIssueItem)}
              </View>
            )}

            {/* Seção de Erros de Combo */}
            {hasComboErrors && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionIcon}>❌</Text>
                  <Text style={styles.sectionTitle}>Erro nos Combos</Text>
                </View>
                <Text style={styles.sectionDescription}>
                  Não foi possível processar os combos abaixo:
                </Text>
                {groupedProducts.comboErrors.map(renderComboErrorItem)}
              </View>
            )}
          </ScrollView>

          {/* Footer com botões */}
          <View style={styles.footer}>
            <Text style={styles.footerHint}>
              {canUpdate 
                ? 'Ao atualizar, as quantidades serão ajustadas para o máximo disponível e os preços serão corrigidos.'
                : 'Os produtos listados serão removidos do carrinho.'
              }
            </Text>
            
            <TouchableOpacity
              style={styles.primaryButton}
              onPress={onUpdateCart}
              activeOpacity={0.8}
            >
              <Text style={styles.primaryButtonText}>
                {canUpdate ? 'Atualizar Carrinho' : 'Remover Produtos'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={onClearCart}
              activeOpacity={0.8}
            >
              <Text style={styles.secondaryButtonText}>Limpar Carrinho</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.textButton}
              onPress={onClose}
              activeOpacity={0.8}
            >
              <Text style={styles.textButtonText}>Voltar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  container: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: SCREEN_WIDTH - 40,
    maxHeight: '85%',
    overflow: 'hidden',
  },
  header: {
    alignItems: 'center',
    paddingTop: 24,
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  iconContainer: {
    marginBottom: 12,
  },
  warningIcon: {
    fontSize: 40,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
  content: {
    maxHeight: 300,
    paddingHorizontal: 16,
  },
  section: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  sectionIcon: {
    fontSize: 18,
    marginRight: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
  },
  sectionDescription: {
    fontSize: 13,
    color: '#888',
    marginBottom: 12,
  },
  itemCard: {
    backgroundColor: '#f8f8f8',
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
  },
  itemName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  priceCompare: {
    gap: 4,
  },
  priceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  priceLabel: {
    fontSize: 13,
    color: '#666',
  },
  oldPrice: {
    fontSize: 14,
    color: '#999',
    textDecorationLine: 'line-through',
  },
  newPrice: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.RedPallete?.red || '#E91E63',
  },
  stockInfo: {
    fontSize: 13,
    color: '#e67e22',
    fontWeight: '500',
  },
  catalogInfo: {
    fontSize: 13,
    color: '#e74c3c',
    fontWeight: '500',
  },
  // Estilos para combo limit
  comboLimitInfo: {
    gap: 4,
  },
  quantityRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  quantityLabel: {
    fontSize: 13,
    color: '#666',
  },
  requestedQty: {
    fontSize: 14,
    color: '#999',
    textDecorationLine: 'line-through',
  },
  availableQty: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.RedPallete?.red || '#E91E63',
  },
  limitanteText: {
    fontSize: 12,
    color: '#888',
    fontStyle: 'italic',
    marginTop: 4,
  },
  comboErrorInfo: {
    fontSize: 13,
    color: '#e74c3c',
    fontWeight: '500',
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  footerHint: {
    fontSize: 12,
    color: '#888',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 18,
  },
  primaryButton: {
    backgroundColor: Colors.RedPallete?.red || '#E91E63',
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
    marginBottom: 10,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButton: {
    backgroundColor: '#f5f5f5',
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
    marginBottom: 10,
  },
  secondaryButtonText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '600',
  },
  textButton: {
    paddingVertical: 8,
    alignItems: 'center',
  },
  textButtonText: {
    color: '#888',
    fontSize: 14,
  },
});

export default UnavailableModal;